import { POSTHOG_FEATURE_FLAGS, useFeatureFlagsReady, usePostHogFeatureFlag } from '../usePostHogFeatureFlag'
import { usePermissions } from '../usePermissions'

/**
 * Shared FGA gating for the `validation_submissions` domain.
 *
 * While `internal_fga_orgv_validation_submissions` is off the backend skips its
 * permission check, so every check here falls open to the pre-FGA behaviour — the same
 * window handling as `AccessRoute` and `resolveNavAccess`.
 *
 * `canReadDocuments` is deliberately separate from `canRead`: `doc_reader` is an extra
 * rung between `reader` and `editor`, so a caller holding `can_read` alone must not see
 * the documents.
 *
 * Every check waits for `useFeatureFlagsReady()`. `usePostHogFeatureFlag` reads an
 * unresolved flag as `false`, which would fall open and render controls that then vanish
 * once the flag lands as on — visible on the pages that have no `AccessRoute` in front of
 * them. Staying closed until the flag is known trades that for a brief absence on a hard
 * load, which is what `AccessRoute` already does with its spinner.
 */
export function useValidationSubmissionsAccess(): {
  canRead: boolean
  canReadDocuments: boolean
  canMarkFraud: boolean
  canReleaseHold: boolean
} {
  const { userHasPermissions } = usePermissions()
  const flagsReady = useFeatureFlagsReady()
  const internalFgaOrgvValidationSubmissionsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VALIDATION_SUBMISSIONS
  )

  const isAllowed = (permission: string) =>
    flagsReady && (!internalFgaOrgvValidationSubmissionsFeatureFlag || userHasPermissions(permission))

  return {
    canRead: isAllowed('validation_submissions:can_read'),
    canReadDocuments: isAllowed('validation_submissions:can_read_documents'),
    canMarkFraud: isAllowed('validation_submissions:can_mark_fraud'),
    // No fall-open: the release route checks this permission whatever the rollout flag says.
    canReleaseHold: userHasPermissions('validation_submissions:can_release_hold')
  }
}
